'use client';

import type { AuthUser } from '@/features/auth/types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CalendarDays, X } from 'lucide-react';
import Title from '../../ui/Title';
import { InputLabel } from '../../ui/InputLabel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getDateFnsLocale } from '@/lib/dateFnsLocale';
import { DateTime } from 'luxon';
import z from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { forgotPassword, logout, updateAuthUser } from '@/features/auth/authThunks';
import { useAppDispatch } from '@/app/hooks';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import EditIcon from '../../icons/EditIcon';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import UpdateProfilPicture from './UpdateProfilPicture';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { PhoneSelectInput } from '../../ui/PhoneSelectInput';
import { CountrySelectSearch } from '../../ui/CountrySelectSearch';

type FormValues = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  birthDate?: string;
  gender?: string;
  country?: string;
};

export default function InfosUpdateForm({
  user,
  phoneRequired = false,
  onClose,
}: {
  user: AuthUser;
  phoneRequired?: boolean;
  onClose: () => void;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dialogOpenEmail, setDialogOpenEmail] = useState(false);
  const [dialogOpenPassword, setDialogOpenPassword] = useState(false);
  const [dialogOpenPicture, setDialogOpenPicture] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { t, i18n } = useTranslation('user');
  // const [isGoogleUser, setIsGoogleUser] = useState(false);

  // useEffect(() => {
  //   const checkGoogleAuth = () => {
  //     const currentUser = auth.currentUser;
  //     if (currentUser) {
  //       const hasGoogleProvider = currentUser.providerData.some(
  //         (provider) => provider.providerId === 'google.com',
  //       );
  //       setIsGoogleUser(hasGoogleProvider);
  //     }
  //   };

  //   checkGoogleAuth();
  //   const unsubscribe = auth.onAuthStateChanged(() => {
  //     checkGoogleAuth();
  //   });

  //   return () => unsubscribe();
  // }, []);

  const schema = useMemo(
    () =>
      z.object({
        firstName: z
          .string()
          .refine((val) => val.trim().length > 0, { message: t('validation.firstName.required') })
          .refine((val) => val.length <= 24, { message: t('validation.firstName.max') }),
        lastName: z
          .string()
          .refine((val) => val.trim().length > 0, { message: t('validation.lastName.required') })
          .refine((val) => val.length <= 24, { message: t('validation.lastName.max') }),
        phoneNumber: z
          .string()
          .min(1, { message: t('validation.phoneNumber.required') })
          .refine(
            (val) => {
              if (!val || val.trim() === '') return true;
              return isValidPhoneNumber(val);
            },
            { message: t('validation.phoneNumber.invalid') },
          )
          .refine(
            (val) => {
              if (!val || val.trim() === '') return true;
              return val.length <= 20;
            },
            { message: t('validation.phoneNumber.maxLength') },
          ),
        birthDate: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/, { message: t('validation.birthDate.format') })
          .refine(
            (date) => {
              const parsedDate = DateTime.fromISO(date);
              return parsedDate.isValid && parsedDate >= DateTime.fromISO('1900-01-01');
            },
            { message: t('validation.birthDate.min') },
          )
          .refine(
            (date) => {
              const parsedDate = DateTime.fromISO(date);
              if (!parsedDate.isValid) return false;

              const today = DateTime.now().startOf('day');
              const maxDate = today.minus({ years: 18 });

              return parsedDate <= maxDate;
            },
            { message: t('validation.birthDate.minAge') },
          )
          .optional(),
        gender: z
          .string()
          .min(1, { message: t('validation.gender.required') })
          .optional(),
        country: z
          .string()
          .min(1, { message: t('validation.country.required') })
          .optional(),
      }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      phoneNumber: user.phoneNumber ?? '',
      birthDate: user.birthDate
        ? user.birthDate
          ? DateTime.fromISO(user.birthDate).toFormat('yyyy-MM-dd')
          : DateTime.fromISO(String(user.birthDate)).toFormat('yyyy-MM-dd')
        : undefined,
      gender: user.gender,
      country: user.country,
    },
    mode: 'onChange',
  });

  // Panneau imposé : on montre tout de suite ce qui manque, sinon le bouton reste grisé sans explication.
  useEffect(() => {
    if (phoneRequired) void form.trigger(['firstName', 'lastName', 'phoneNumber']);
  }, [phoneRequired, form]);

  const isValid = form.formState.isValid;

  const onSubmit = async (data: FormValues) => {
    setIsSaving(true);
    try {
      console.log(data);
      const res = await dispatch(
        updateAuthUser({
          lastName: data.lastName.trim(),
          firstName: data.firstName.trim(),
          phoneNumber: data.phoneNumber?.trim() || '',
          birthDate: data.birthDate,
          gender: data.gender,
          country: data.country?.trim() || undefined,
          expertiseDomain: user.expertiseDomain,
          expertiseSubdomain: user.expertiseSubdomain,
          usedOtherWebinarTool:
            typeof user.usedOtherWebinarTool === 'string' ? user.usedOtherWebinarTool : undefined,
          monthlyRevenue: (user as AuthUser & { monthlyRevenue?: string }).monthlyRevenue,
        }),
      );
      if (updateAuthUser.fulfilled.match(res)) {
        toast.success(t('toast.update.success'));
        onClose();
      } else if (updateAuthUser.rejected.match(res)) {
        const error = res.payload;
        if (error?.status === 400) {
          toast.error(t('toast.update.validationError'));

          if (error.details && Array.isArray(error.details)) {
            error.details.forEach((err: { message?: string; fieldName?: string }) => {
              if (err.message) {
                toast.error(err.message);
              }
            });
          }
        } else {
          toast.error(t('toast.update.error'));
        }
      }
    } catch (error) {
      toast.error(t('toast.update.error'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="flex items-center gap-4 p-4 sm:p-6 bg-[#EAF0FF] rounded-tl-[20px]">
        {!phoneRequired && (
          <X className="size-4 cursor-pointer absolute z-50 top-4 right-4" onClick={onClose} />
        )}

        <Dialog open={dialogOpenPicture} onOpenChange={setDialogOpenPicture}>
          <DialogTrigger asChild>
            <div className="group relative inline-block">
              <Avatar className="size-16 cursor-pointer">
                <AvatarImage src={user.pictureUrl} />
                <AvatarFallback>
                  {user.firstName?.[0].toUpperCase()}
                  {user.lastName?.[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                <EditIcon className="size-10 text-white" />
              </div>
            </div>
          </DialogTrigger>
          <DialogContent className="max-w-[800px]">
            <DialogHeader>
              <DialogTitle>{t('picture.title')}</DialogTitle>
            </DialogHeader>

            <UpdateProfilPicture user={user} onUploadSuccess={() => setDialogOpenPicture(false)} />
          </DialogContent>
        </Dialog>

        <p className="text-2xl font-semibold">
          {user.firstName} {user.lastName}
        </p>
      </div>

      <div className="space-y-8 p-4 sm:p-6">
        {phoneRequired && (
          <p role="alert" className="rounded-lg bg-[#EAF0FF] p-4 text-sm text-foreground">
            {t('phoneRequired.message')}
          </p>
        )}
        <div className="space-y-4">
          <Title level={2}>{t('personalData.title')}</Title>
          <div className="space-x-4 flex items-start">
            <div className="flex flex-col w-full">
              <Controller
                name="lastName"
                control={form.control}
                render={({ field }) => (
                  <InputLabel
                    label={`${t('personalData.lastName.label')}*`}
                    value={field.value}
                    onChange={(e) => {
                      const newValue = e.target.value.slice(0, 25);
                      field.onChange(newValue);
                    }}
                    maxLength={25}
                    className="w-full"
                  />
                )}
              />
              <div>
                {form.formState.errors.lastName && (
                  <p className="text-xs text-error mt-1">
                    {form.formState.errors.lastName.message}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col w-full">
              <Controller
                name="firstName"
                control={form.control}
                render={({ field }) => (
                  <InputLabel
                    label={`${t('personalData.firstName.label')}*`}
                    value={field.value}
                    onChange={(e) => {
                      const newValue = e.target.value.slice(0, 25);
                      field.onChange(newValue);
                    }}
                    maxLength={25}
                    className="w-full"
                  />
                )}
              />
              <div>
                {form.formState.errors.firstName && (
                  <p className="text-xs text-error mt-1">
                    {form.formState.errors.firstName.message}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div
            className={
              phoneRequired ? 'space-y-1 rounded-lg ring-2 ring-primary ring-offset-4' : 'space-y-1'
            }
          >
            <Controller
              name="phoneNumber"
              control={form.control}
              render={({ field }) => (
                <PhoneSelectInput
                  defaultCountry="FR"
                  value={field.value || undefined}
                  onChange={(value) => {
                    field.onChange(value || '');
                  }}
                  label={`${t('personalData.phoneNumber.label')}*`}
                  className="w-full"
                  theme={'LIGHT'}
                />
              )}
            />
            {form.formState.errors.phoneNumber && (
              <p className="text-xs text-error">{form.formState.errors.phoneNumber.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-4 ">
          <Title level={2}>{t('generalInfo.title')}</Title>

          <div className="flex flex-col gap-3 w-full">
            <Controller
              name="birthDate"
              control={form.control}
              render={({ field }) => {
                const selectedDate = field.value
                  ? DateTime.fromISO(field.value).toJSDate()
                  : undefined;

                const today = new Date();
                const defaultMonthDate = new Date(today);
                defaultMonthDate.setFullYear(today.getFullYear() - 18);
                defaultMonthDate.setHours(0, 0, 0, 0);

                const defaultMonth = selectedDate || defaultMonthDate;

                return (
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <div className="border text-tertiary border-input rounded-lg px-4 py-2 h-14 flex items-center justify-between cursor-pointer">
                        {field.value ? (
                          <>
                            <div>
                              <p className="text-sm">{t('generalInfo.birthDate.label')}</p>
                              <p className="text-black">
                                {DateTime.fromISO(field.value).toFormat('dd/MM/yyyy')}
                              </p>
                            </div>
                            <CalendarDays color="black" size={24} />
                          </>
                        ) : (
                          <>
                            {t('generalInfo.birthDate.label')}
                            <CalendarDays color="black" size={24} />
                          </>
                        )}
                      </div>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-(--radix-popover-trigger-width) overflow-hidden p-0 border-input"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        defaultMonth={defaultMonth}
                        captionLayout="dropdown"
                        locale={getDateFnsLocale(i18n.language)}
                        className="w-full"
                        classNames={{
                          day: 'w-full ',
                        }}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const dateToCheck = new Date(date);
                          dateToCheck.setHours(0, 0, 0, 0);

                          if (dateToCheck >= today) {
                            return true;
                          }

                          const maxDate = new Date(today);
                          maxDate.setFullYear(today.getFullYear() - 18);
                          maxDate.setHours(0, 0, 0, 0);

                          return dateToCheck > maxDate;
                        }}
                        onSelect={(date) => {
                          if (date) {
                            field.onChange(DateTime.fromJSDate(date).toFormat('yyyy-MM-dd'));
                            setOpen(false);
                          }
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                );
              }}
            />
            {form.formState.errors.birthDate && (
              <p className="text-xs text-error">{form.formState.errors.birthDate.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Controller
              name="country"
              control={form.control}
              render={({ field }) => (
                <CountrySelectSearch
                  value={field.value}
                  onChange={field.onChange}
                  label={t('generalInfo.country.label')}
                  className="w-full"
                  valueFormat="name"
                />
              )}
            />
            {form.formState.errors.country && (
              <p className="text-xs text-error">{form.formState.errors.country.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Controller
              name="gender"
              control={form.control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger label={t('generalInfo.gender.label')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MALE">{t('generalInfo.gender.options.male')}</SelectItem>
                    <SelectItem value="FEMALE">{t('generalInfo.gender.options.female')}</SelectItem>
                    <SelectItem value="UNKNOWN">{t('generalInfo.gender.options.other')}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {form.formState.errors.gender && (
              <p className="text-xs text-error">{form.formState.errors.gender.message}</p>
            )}
          </div>
        </div>

        {/* {!isGoogleUser && ( */}
        <div className="space-y-4 ">
          <Title level={2}>{t('credentials.title')}</Title>

          <div className="flex items-center gap-4">
            <InputLabel
              label={t('credentials.email.label')}
              value={user.email}
              className="w-full shrink"
              disabled
            />

            <Dialog open={dialogOpenEmail} onOpenChange={setDialogOpenEmail}>
              <DialogTrigger asChild>
                <p className="text-base font-medium cursor-pointer  ">{t('actions.edit')}</p>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('credentials.email.dialog.title')}</DialogTitle>
                </DialogHeader>
                <p className="text-base text-secondary pb-8">
                  {t('credentials.email.dialog.message')}
                </p>

                <div className="flex items-center gap-4 w-full">
                  <div className="w-1/2">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setDialogOpenEmail(false)}
                    >
                      {t('actions.cancel')}
                    </Button>
                  </div>
                  <div className="w-1/2">
                    <Button
                      className="w-full"
                      variant="default"
                      onClick={() => {
                        setDialogOpenEmail(false);
                        window.location.href = 'mailto:contact@welya.io';
                      }}
                    >
                      {t('credentials.email.dialog.contactSupport')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="flex items-center gap-4">
            <InputLabel
              label={t('credentials.password.label')}
              value={'****************'}
              className="w-full shrink "
              disabled
            />

            <Dialog open={dialogOpenPassword} onOpenChange={setDialogOpenPassword}>
              <DialogTrigger asChild>
                <p className="text-base font-medium cursor-pointer">{t('actions.edit')}</p>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('credentials.password.dialog.title')}</DialogTitle>
                </DialogHeader>

                <p className="text-base text-secondary pb-8">
                  {t('credentials.password.dialog.message')}
                </p>

                <div className="flex items-center gap-4 w-full">
                  <div className="w-1/2">
                    <Button
                      variant="outline"
                      onClick={() => setDialogOpenPassword(false)}
                      className="w-full"
                    >
                      {t('actions.cancel')}
                    </Button>
                  </div>
                  <div className="w-1/2">
                    <Button
                      variant="default"
                      onClick={async () => {
                        try {
                          const result = await dispatch(forgotPassword({ email: user.email }));
                          if (forgotPassword.fulfilled.match(result)) {
                            setDialogOpenPassword(false);
                            const logoutResult = await dispatch(logout());
                            if (logout.fulfilled.match(logoutResult)) {
                              navigate('/login', { replace: true });
                            }
                          } else if (forgotPassword.rejected.match(result)) {
                            toast.error(t('toast.passwordReset.error'));
                          }
                        } catch (error) {
                          toast.error(t('toast.passwordReset.error'));
                        }
                      }}
                      className="w-full"
                    >
                      {t('credentials.password.dialog.continue')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        {/* )} */}

        <div className="flex gap-4">
          <Button
            className="h-12 flex-1 text-base disabled:bg-button-primary-disable-inverse disabled:text-disable"
            variant="outline"
            onClick={onClose}
            type="button"
          >
            {t('actions.cancel')}
          </Button>
          <Button className="h-12 flex-1 text-base" type="submit" disabled={!isValid || isSaving}>
            {isSaving ? t('actions.saving') : t('actions.save')}
          </Button>
        </div>
      </div>
    </form>
  );
}
